import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Switch,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ErrorObject } from '../../../../types/errorhandling.types';
import type { BrandingResponse } from '../../../../types/Branding.types';
import { StyledQuestionMark } from '../../../../components/QuestionMark';
import { TextFieldWithHelp } from '../GeneralTab/components/TextFieldWithHelp';
import { ColumnOrderList } from './ColumnOrderList';
import { COLUMN_KEYS, ColumnKey, reconcileColumnOrder } from './columnDefs';

interface DisplaySettingsData {
  publishingModelUrl: string;
  licensesUrl: string;
  customLicenseUrl: string;
  accessibilityUrl: string;
  openInNewWindow: boolean;
  searchTips: boolean;
  showLastUpdatedAt: boolean;
  showAgreementDescription: boolean;
  showSearchSuggestions: boolean;
  columnOrder?: string[];
}

interface DisplaySettingsCallbacks {
  updateField: (field: keyof DisplaySettingsData, value: unknown) => void;
}

type DisplaySettingsTabProps = {
  data: DisplaySettingsData;
  callbacks: DisplaySettingsCallbacks;
  readonly: boolean;
  fieldErrors?: ErrorObject;
  branding?: BrandingResponse;
};

const columnWidth = { xs: '100%', md: '90%', lg: '75%' };

const DisplaySettingsTab = memo(
  ({
    data,
    callbacks,
    readonly,
    fieldErrors = {},
    branding,
  }: DisplaySettingsTabProps) => {
    const { t } = useTranslation();
    const isFieldDisabled = readonly;

    // Matches the same "has quality columns" check the public results
    // table uses (apps/search's hasQualityExtension) so the admin reorder
    // control and the public page always agree on whether Quality is
    // orderable/shown. Note this deliberately does NOT reuse the
    // key-presence-only `hasExtension` helper: an account with
    // `extensions.quality_lists: []` would count as "has the extension"
    // there, but the public page has nothing to show for an empty list.
    const applicableKeys: ColumnKey[] =
      (branding?.extensions?.quality_lists?.length ?? 0) > 0
        ? COLUMN_KEYS
        : COLUMN_KEYS.filter((key) => key !== 'quality');
    const order = reconcileColumnOrder(data.columnOrder, applicableKeys);

    const handleReorder = (newOrder: ColumnKey[]) => {
      callbacks.updateField('columnOrder', newOrder);
    };

    return (
      <Box>
        <Grid container spacing={{ xs: 3, md: 4, lg: 6 }}>
          {/* Left Column: URL fields */}
          <Grid item xs={12} md={6}>
            <Box sx={{ width: columnWidth, pr: { xs: 0, md: 2 }, mt: 32 }}>
              <Typography
                component="h2"
                variant="h5"
                gutterBottom
                id="url-configuration-section"
              >
                {t('branding.sections.urlConfiguration')}
              </Typography>

              <TextFieldWithHelp
                name="publishingModelUrl"
                label={t('branding.fields.publishing_model_url')}
                type="url"
                value={data.publishingModelUrl}
                onChange={(e) =>
                  callbacks.updateField('publishingModelUrl', e.target.value)
                }
                disabled={isFieldDisabled}
                error={!!fieldErrors.publishing_model_url}
                errorText={fieldErrors.publishing_model_url?.[0]}
                helpText={t('branding.help.publishing_model_url') as string}
                onReset={() =>
                  callbacks.updateField('publishingModelUrl', '')
                }
              />

              <TextFieldWithHelp
                name="licensesUrl"
                label={t('branding.fields.licenses_url')}
                type="url"
                value={data.licensesUrl}
                onChange={(e) =>
                  callbacks.updateField('licensesUrl', e.target.value)
                }
                disabled={isFieldDisabled}
                error={!!fieldErrors.licenses_url}
                errorText={fieldErrors.licenses_url?.[0]}
                helpText={t('branding.help.licenses_url') as string}
                onReset={() => callbacks.updateField('licensesUrl', '')}
              />

              <TextFieldWithHelp
                name="customLicenseUrl"
                label={t('branding.fields.custom_license_url')}
                type="url"
                value={data.customLicenseUrl}
                onChange={(e) =>
                  callbacks.updateField('customLicenseUrl', e.target.value)
                }
                disabled={isFieldDisabled}
                error={!!fieldErrors.custom_license_url}
                errorText={fieldErrors.custom_license_url?.[0]}
                helpText={t('branding.help.custom_license_url') as string}
                onReset={() => callbacks.updateField('customLicenseUrl', '')}
              />

              <TextFieldWithHelp
                name="accessibilityUrl"
                label={t('branding.fields.accessibility_url')}
                type="url"
                value={data.accessibilityUrl}
                onChange={(e) =>
                  callbacks.updateField('accessibilityUrl', e.target.value)
                }
                disabled={isFieldDisabled}
                error={!!fieldErrors.accessibility_url}
                errorText={fieldErrors.accessibility_url?.[0]}
                helpText={t('branding.help.accessibility_url') as string}
                onReset={() => callbacks.updateField('accessibilityUrl', '')}
              />
            </Box>
          </Grid>

          {/* Right Column: Column order */}
          <Grid item xs={12} md={6}>
            <Box sx={{ width: columnWidth, pl: { xs: 0, md: 2 }, mt: 32 }}>
              <Typography
                component="h2"
                variant="h5"
                gutterBottom
                id="column-order-section"
              >
                {t('branding.sections.columnOrder')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 16 }}>
                Choose the order in which columns appear on the public search
                results table.
              </Typography>
              {fieldErrors.column_order && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  {fieldErrors.column_order[0]}
                </Typography>
              )}
              <ColumnOrderList
                order={order}
                onReorder={handleReorder}
                disabled={isFieldDisabled}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Toggles: occupy the same space as the left column, below both */}
        <Grid container spacing={{ xs: 3, md: 4, lg: 6 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ width: columnWidth, mt: 32 }}>
              <Typography
                component="h2"
                variant="h5"
                gutterBottom
                id="behavior-section"
              >
                {t('branding.sections.behavior')}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={data.openInNewWindow}
                      onChange={(e) =>
                        callbacks.updateField(
                          'openInNewWindow',
                          e.target.checked,
                        )
                      }
                      disabled={isFieldDisabled}
                    />
                  }
                  label={t('branding.fields.open_in_new_window')}
                />
                <Tooltip
                  title={t('branding.help.open_in_new_window')}
                  placement="top"
                >
                  <IconButton size="small" sx={{ marginLeft: -12 }}>
                    <StyledQuestionMark fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.searchTips}
                      onChange={(e) =>
                        callbacks.updateField('searchTips', e.target.checked)
                      }
                      disabled={isFieldDisabled}
                    />
                  }
                  label={t('branding.fields.search_tips')}
                />
                <Tooltip
                  title={t('branding.help.search_tips')}
                  placement="top"
                >
                  <IconButton size="small" sx={{ marginLeft: -12 }}>
                    <StyledQuestionMark fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.showLastUpdatedAt}
                      onChange={(e) =>
                        callbacks.updateField(
                          'showLastUpdatedAt',
                          e.target.checked,
                        )
                      }
                      disabled={isFieldDisabled}
                    />
                  }
                  label={t('branding.fields.show_last_updated_at')}
                />
                <Tooltip
                  title={t('branding.help.show_last_updated_at')}
                  placement="top"
                >
                  <IconButton size="small" sx={{ marginLeft: -12 }}>
                    <StyledQuestionMark fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.showAgreementDescription}
                      onChange={(e) =>
                        callbacks.updateField(
                          'showAgreementDescription',
                          e.target.checked,
                        )
                      }
                      disabled={isFieldDisabled}
                    />
                  }
                  label="Show public agreement description"
                />
                <Tooltip
                  title="When selected, the agreement description will show in the search page. Deselect to hide the agreement description from the search page."
                  placement="top"
                >
                  <IconButton size="small" sx={{ marginLeft: -12 }}>
                    <StyledQuestionMark fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.showSearchSuggestions}
                      onChange={(e) =>
                        callbacks.updateField(
                          'showSearchSuggestions',
                          e.target.checked,
                        )
                      }
                      disabled={isFieldDisabled}
                    />
                  }
                  label={t('branding.fields.show_search_suggestions')}
                />
                <Tooltip
                  title={t('branding.help.show_search_suggestions')}
                  placement="top"
                >
                  <IconButton size="small" sx={{ marginLeft: -12 }}>
                    <StyledQuestionMark fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  },
);

DisplaySettingsTab.displayName = 'DisplaySettingsTab';

export default DisplaySettingsTab;
